'use client';

import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export type Cliente = {
  id_usuario: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  ultima_cita?: string | null;
};

export function useClientes() {
  const idUsuario = localStorage.getItem('id_usuario') || '';

  // ============================================================
  // 1) AGREGAR CLIENTE A empresa_clientes
  // ============================================================
  const agregarCliente = useCallback(
    async (
      id_empresa: string,
      email: string,
      event?: any,
      refresh?: () => void
    ) => {
      const toastId = toast.loading('Añadiendo cliente...');

      // 1) Obtener ID mediante RPC
      const { data: id_usuario, error: rpcError } = await supabase.rpc(
        'obtener_id_usuario_por_email',
        { email_input: email }
      );

      if (rpcError) {
        Sentry.captureException(rpcError, { extra: { email, id_empresa } }); // 👈
        toast.dismiss(toastId);
        toast.error('Error buscando usuario');
        return;
      }

      if (!id_usuario) {
        toast.dismiss(toastId);
        toast.warning(`No existe ningún usuario con el email ${email}`);
        return;
      }

      // 2) Insertar en empresa_clientes
      const { error } = await supabase.from('empresa_clientes').insert([
        {
          id_empresa,
          id_usuario,
          usuario_creacion: idUsuario,
          activo: true,
        },
      ]);

      toast.dismiss(toastId);

      if (error) {
        Sentry.captureException(error, { extra: { id_empresa, id_usuario } }); // 👈
        toast.error('No se pudo añadir el cliente.');
        return;
      }

      toast.success('Cliente añadido correctamente.');

      if (event?.setExtendedProp) {
        event.setExtendedProp('cliente_sugerido', false);
      }

      refresh?.();
    },
    [idUsuario]
  );

  // ============================================================
  // 2) OBTENER CLIENTES DE LA EMPRESA
  // ============================================================
  const getClientesEmpresa = useCallback(async (id_empresa: string) => {
    const { data, error } = await supabase
      .from('vista_empresa_clientes')
      .select('*')
      .eq('id_empresa', id_empresa);

    if (error) {
      Sentry.captureException(error, { extra: { id_empresa } }); // 👈
      toast.error('Error al cargar clientes');
      return [];
    }

    return data as Cliente[];
  }, []);

  // ============================================================
  // 3) OBTENER CLIENTES RECIENTES (NO AÑADIDOS)
  // ============================================================
  const getClientesRecientes = useCallback(async (id_empresa: string) => {
    const { data, error } = await supabase
      .from('vista_ultimos_clientes_sugeridos')
      .select('*')
      .eq('id_empresa', id_empresa)
      .order('ultima_cita', { ascending: false });

    if (error) {
      Sentry.captureException(error, { extra: { id_empresa } }); // 👈
      toast.error('Error al cargar últimos clientes');
      return [];
    }

    return data as Cliente[];
  }, []);

  // ============================================================
  // 4) BORRAR CLIENTE DE empresa_clientes
  // ============================================================
  const borrarCliente = useCallback(
    async (id_empresa: string, id_usuario: string, refresh?: () => void) => {
      const toastId = toast.loading('Eliminando cliente...');

      const { error } = await supabase
        .from('empresa_clientes')
        .delete()
        .eq('id_empresa', id_empresa)
        .eq('id_usuario', id_usuario);

      toast.dismiss(toastId);

      if (error) {
        Sentry.captureException(error, { extra: { id_empresa, id_usuario } }); // 👈
        toast.error('No se pudo eliminar el cliente');
        return;
      }

      toast.success('Cliente eliminado correctamente');

      refresh?.();
    },
    []
  );

  // ============================================================
  // 5) BUSCAR CLIENTE EN vista_clientes (email o teléfono)
  // ============================================================
  const buscarClienteVista = useCallback(
    async (query: string): Promise<Cliente | null> => {
      if (!query.trim()) return null;

      const { data, error } = await supabase
        .from('vista_clientes')
        .select('*')
        .or(`email.ilike.%${query}%,telefono.ilike.%${query}%`)
        .limit(1);

      if (error) {
        Sentry.captureException(error, { extra: { query } }); // 👈
        toast.error('Error buscando cliente');
        return null;
      }

      if (!data || data.length === 0) return null;

      const u = data[0];

      return {
        id_usuario: u.id_usuario,
        nombre: u.nombre || '(sin nombre)',
        email: u.email,
        telefono: u.telefono,
        ultima_cita: u.ultima_cita ?? null,
      };
    },
    []
  );

  // ============================================================
  // RETURN
  // ============================================================
  return {
    agregarCliente,
    getClientesEmpresa,
    getClientesRecientes,
    borrarCliente,
    buscarClienteVista,
  };
}
